import { useParams, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { ChatArea } from '@/components/chat/ChatArea.js';
import { ChatTabBar } from '@/components/chat/ChatTabBar.js';
import { useServices } from '@/contexts/ServicesContext.js';
import { useSessionStore } from '@/stores/sessionStore.js';
import { useChatTabsStore } from '@/stores/chatTabsStore.js';

export function ChatPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const { sessionService } = useServices();
  const currentSessionId = useSessionStore((state) => state.currentSessionId);
  const isCreatingSession = useSessionStore((state) => state.isCreatingSession);
  const addTab = useChatTabsStore((state) => state.addTab);

  // Handle /chat/new route - redirect to actual session once created
  useEffect(() => {
    if (!sessionId && currentSessionId && !isCreatingSession) {
      console.log('[ChatPage] Session created, redirecting to:', currentSessionId);
      navigate(`/chat/${currentSessionId}`, { replace: true });
    }
  }, [sessionId, currentSessionId, isCreatingSession, navigate]);

  useEffect(() => {
    // Simple rule: If URL has a sessionId that's different from current, switch to it
    // This handles: direct navigation, clicking sessions in history, browser back/forward
    if (sessionId && sessionId !== currentSessionId) {
      console.log('[ChatPage] Switching to session:', sessionId, {
        from: currentSessionId,
        to: sessionId
      });
      sessionService.switchSession(sessionId).catch((err) => {
        console.error('Failed to switch session:', err);
        navigate('/');
      });

      // Add session to tabs if not already there
      // Title will be generic initially, but will be updated when session data loads
      addTab({
        id: `session-${sessionId}`,
        title: `Chat ${sessionId.slice(0, 8)}`,
        sessionId,
        isPinned: false,
      });
    }
    // We deliberately exclude currentSessionId from dependencies to avoid
    // re-triggering when the store updates (which happens during switchSession)
    // or when it's cleared (during delete/navigate away)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, sessionService, navigate, addTab]);

  return (
    <main className="flex-1 overflow-hidden flex flex-col">
      <ChatTabBar />
      <div className="flex-1 overflow-hidden">
        <ChatArea />
      </div>
    </main>
  );
}
